import React from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import {
  RiMusic2Line,
  RiFileCopy2Line,
  RiArchiveLine,
  RiUserVoiceLine,
  RiTeamLine,
  RiMegaphoneLine,
  RiFileExcel2Line,
  RiListCheck,
  RiToolsLine,
  RiBookOpenLine,
  RiMailSendLine,
} from "react-icons/ri";

const MENU_ITEMS = [
  {
    href: "/admin/songs",
    icon: RiMusic2Line,
    iconColor: "#5e72e4",
    iconBg: "#eaecfb",
    title: "Manage Songs",
    description: "Add, edit and delete songs. Update titles, scripture references, themes and song-book associations.",
  },
  {
    href: "/admin/song-books",
    icon: RiBookOpenLine,
    iconColor: "#2dce89",
    iconBg: "#d6f5e8",
    title: "Manage Song Books",
    description: "View song books and bulk update song numbers for songs in each book.",
  },
  {
    href: "/admin/song-duplicates",
    icon: RiFileCopy2Line,
    iconColor: "#8965e0",
    iconBg: "#ede7f6",
    title: "Duplicate Songs",
    description: "Identify possible duplicate songs across songbooks. Review matches by confidence score before any manual cleanup.",
  },
  {
    href: "/admin/themes",
    icon: RiArchiveLine,
    iconColor: "#2dce89",
    iconBg: "#d6f5e8",
    title: "Manage Themes",
    description: "Create and edit worship themes. Link themes to songs across the library.",
  },
  {
    href: "/admin/authors",
    icon: RiUserVoiceLine,
    iconColor: "#fb6340",
    iconBg: "#fde5de",
    title: "Manage Authors",
    description: "Add and edit authors. Associate authors with their songs in the library.",
  },
  {
    href: "/admin/members",
    icon: RiTeamLine,
    iconColor: "#11cdef",
    iconBg: "#d4f6fc",
    title: "Manage Members",
    description: "View and manage registered user accounts and their access levels.",
  },
  {
    href: "/admin/marketing",
    icon: RiMegaphoneLine,
    iconColor: "#f4a100",
    iconBg: "#fef3d8",
    title: "Marketing Campaigns",
    description: "Create and manage QR code campaigns and marketing materials.",
  },
  {
    href: "/admin/import",
    icon: RiFileExcel2Line,
    iconColor: "#1d7a3a",
    iconBg: "#d4edda",
    title: "Spreadsheet Import",
    description: "Upload song data and musical metadata spreadsheets in .xlsx or .zip format.",
  },
  {
    href: "/admin/service-plan-templates",
    icon: RiListCheck,
    iconColor: "#5e72e4",
    iconBg: "#eaecfb",
    title: "Service Plan Templates",
    description: "Create and manage reusable service plan templates. Control which templates are visible to users.",
  },
  {
    href: "/admin/curated-song-flows",
    icon: RiMailSendLine,
    iconColor: "#2dce89",
    iconBg: "#d6f5e8",
    title: "Published Song-Flows",
    description: "Publish curated song-flows, manage recipients, and send curated worship plans by email.",
  },
  {
    href: "/admin/operations",
    icon: RiToolsLine,
    iconColor: "#525f7f",
    iconBg: "#e9ecef",
    title: "Operations",
    description: "Investigate organisation accounts, billing periods, lifecycle state and support actions.",
  },
];

class AdminMenu extends React.Component {
  state = {
    unauthorized: false,
  };

  componentDidMount() {
    try {
      const customer = sessionStorage.getItem("customer");
      const parsed = customer ? JSON.parse(customer) : null;
      if (!parsed?.user?.isAdmin) {
        this.setState({ unauthorized: true });
      }
    } catch (e) {
      this.setState({ unauthorized: true });
    }
  }

  render() {
    if (!sessionStorage.getItem("customer")) {
      return sessionEnded();
    }
    if (this.state.unauthorized) return <Navigate to="/" />;

    return (
      <>
        <SeoNoIndex noindex={true} />

        <div className="position-relative">
          <section className="section section-lg section-shaped pb-250">
            <div className="shape shape-style-1 shape-default">
              <span/><span/><span/><span/><span/><span/><span/><span/><span/>
            </div>
            <Container className="py-lg-md d-flex">
              <div className="col px-0">
                <Row>
                  <Col lg="8">
                    <h1 className="display-3 text-white">
                      The Worship Bible <span>Admin</span>
                    </h1>
                    <p className="lead text-white">
                      Manage content, users and data imports.
                    </p>
                  </Col>
                </Row>
              </div>
            </Container>
            <div className="separator separator-bottom separator-skew">
              <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                <polygon className="fill-white" points="2560 0 2560 100 0 100"/>
              </svg>
            </div>
          </section>
        </div>

        <section className="section section-lg pt-lg-0 mt--200">
          <Container>
            <Row className="justify-content-center">
              <Col lg="12">
                <Row className="row-grid">
                  {MENU_ITEMS.map(({ href, icon: Icon, iconColor, iconBg, title, description }) => (
                    <Col key={href} lg="4" md="6" className="mb-4">
                      <a href={href} style={{ textDecoration: "none" }}>
                        <Card
                          className="shadow border-0 h-100"
                          style={{ cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s" }}
                          onMouseEnter={e => {
                            e.currentTarget.style.transform = "translateY(-4px)";
                            e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.12)";
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "";
                          }}
                        >
                          <CardBody className="py-4 px-4">
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                backgroundColor: iconBg,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: "1rem",
                              }}
                            >
                              <Icon size={24} color={iconColor} />
                            </div>
                            <h5
                              className="text-uppercase mb-1"
                              style={{ fontSize: "0.85rem", letterSpacing: "0.05em", color: iconColor }}
                            >
                              {title}
                            </h5>
                            <p className="text-muted mb-0" style={{ fontSize: "0.875rem", lineHeight: 1.5 }}>
                              {description}
                            </p>
                          </CardBody>
                        </Card>
                      </a>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Container>
        </section>
      </>
    );
  }
}

function withRouterParamsAndNavigate(Component) {
  return function Wrapped(props) {
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} navigate={navigate} params={params} />;
  };
}

export default withRouterParamsAndNavigate(AdminMenu);
